import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { MeetingDefinition } from "src/types";
import { useTeam, useTeams } from "src/queries";
import { useDebounce } from "src/hooks";
import { useWebinarPromotionRoutingLogic } from "src/queries/useWebinarPromotionRoutingLogic";
import WebinarPromotionTeamList from "./WebinarPromotionTeamList";
import { useUpdateWebinarPromotionTeamId } from "src/mutations";

// Compact, self-contained block to control webinar promotion sender/routing
// Writes to webinarConfig: promotionJustMe, promotionTeamId, promotionRouting, promotionRoutingField
interface WebinarPromotionSettingsProps {
  templateId?: string | number;
}

export default function WebinarPromotionSettings({
  templateId,
}: WebinarPromotionSettingsProps = {}) {
  const { control, getValues, setValue, watch, formState, clearErrors } =
    useFormContext<MeetingDefinition>();

  const [search, setSearch] = useState("");
  const [updatingTeamMembers, setUpdatingTeamMembers] = useState(false);
  const debouncedSearch = useDebounce(encodeURIComponent(search), 500);
  const { data: teams } = useTeams(100, 0, { qry: debouncedSearch });
  const updatePromotionTeamId = useUpdateWebinarPromotionTeamId();

  // Get selected team ID for display and watch for changes
  const selectedTeamId = watch("webinarConfig.promotionTeamId");
  const promotionJustMe = watch("webinarConfig.promotionJustMe");

  // Fetch the team by ID in case it's not in the first page of useTeams (limit 100)
  const { data: teamById } = useTeam(selectedTeamId as any);

  // Fetch promotion routing logic using template ID (only when team is selected)
  const {
    data: promotionTeamMembers,
    loading: loadingTeamMembers,
    mutate: refetchTeamMembers,
  } = useWebinarPromotionRoutingLogic(
    selectedTeamId && templateId ? templateId : undefined,
  );

  // Backfill promotionTeam object when only ID exists (for Autocomplete stability)
  // Ensures the label displays even if the selected team is not in the first page (>100)
  useEffect(() => {
    const obj = getValues("webinarConfig.promotionTeam" as any);
    const teamId = getValues("webinarConfig.promotionTeamId");

    if (!obj && teamId) {
      const fromList = teams?.data?.find((t) => t.id === teamId);
      const found = fromList || teamById;
      if (found) {
        setValue(
          "webinarConfig.promotionTeam" as any,
          { id: found.id, name: found.name },
          { shouldDirty: false, shouldValidate: true },
        );
        setSearch(found.name ?? "");
      }
    }
  }, [
    getValues,
    setValue,
    selectedTeamId,
    teams,
    teamById,
    setSearch,
    formState.isSubmitSuccessful,
  ]);

  // Initialize default
  useEffect(() => {
    const current = getValues("webinarConfig.promotionJustMe");
    if (current === undefined) {
      setValue("webinarConfig.promotionJustMe", true, {
        shouldDirty: false,
      });
    }
  }, [getValues, setValue]);

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        The Messages Promoting This Event Should Come From
      </Typography>
      <Controller
        control={control}
        name={"webinarConfig.promotionJustMe"}
        render={({ field }) => (
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "left",
              justifyContent: "left",
              mb: 3,
            }}
          >
            <Typography
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whitespace: "nowrap",
                width: "320px",
              }}
            >
              Send promotion emails from the template owner only
            </Typography>
            <Switch
              sx={{
                mt: -1,
                mb: 1,
              }}
              checked={!!field.value}
              onChange={(event) => {
                field.onChange(event.target.checked);
                if (event.target.checked) {
                  // Clear team/routing-specific selections when switching to "just me"
                  setValue("webinarConfig.promotionTeamId", null);
                  setValue("webinarConfig.promotionTeam" as any, null);
                  setValue("webinarConfig.promotionRouting", null);
                  setValue("webinarConfig.promotionRoutingField", null);
                  // Clear any validation errors for these fields
                  clearErrors("webinarConfig.promotionTeamId");
                  clearErrors("webinarConfig.promotionTeam" as any);
                  clearErrors("webinarConfig.promotionRouting");
                  clearErrors("webinarConfig.promotionRoutingField");
                }
              }}
            />
          </Stack>
        )}
      />

      {/* Team + Routing controls when switch is OFF (not "just me") */}
      {!watch("webinarConfig.promotionJustMe" as any) && (
        <Stack sx={{ gap: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            Teams
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Select which team will the promotion emails be sent from and how
            will they be routed.
          </Typography>

          {/* All three dropdowns in one row */}
          <Stack
            sx={{
              flexDirection: { xs: "column", lg: "row" },
              gap: 3,
              alignItems: "flex-start",
            }}
          >
            {/* Team Selection */}
            <FormControl sx={{ minWidth: 300 }}>
              <Controller
                control={control}
                name={"webinarConfig.promotionTeam" as any}
                rules={{ required: "Please select a Team" }}
                render={({ field: teamField, fieldState: { error } }) => (
                  <Autocomplete
                    value={teamField.value || null}
                    onChange={async (_, newValue) => {
                      const teamObject = newValue
                        ? { id: newValue.id, name: newValue.name }
                        : null;

                      // Keep an object in the form for stability with Autocomplete
                      teamField.onChange(teamObject);

                      // Sync displayed input text with the selected option label
                      setSearch(newValue ? (newValue.name ?? "") : "");

                      // Maintain the ID field for backend save
                      setValue(
                        "webinarConfig.promotionTeamId",
                        newValue ? newValue.id : null,
                        { shouldDirty: true },
                      );

                      // Update promotion team ID in backend and fetch team members
                      if (newValue && templateId) {
                        try {
                          setUpdatingTeamMembers(true);
                          await updatePromotionTeamId(templateId, newValue.id);
                          await refetchTeamMembers();
                        } catch (error) {
                          console.error(
                            "Failed to update promotion team ID or fetch team members:",
                            error,
                          );
                        } finally {
                          setUpdatingTeamMembers(false);
                        }
                      }
                    }}
                    inputValue={search}
                    onInputChange={(_, newValue, reason) => {
                      // Only update search if user is typing, not when clearing or selecting
                      if (reason === "input") {
                        setSearch(newValue);
                      }
                    }}
                    filterOptions={(options) => options}
                    options={teams?.data ?? []}
                    getOptionLabel={(option) => option.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select a Team"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    sx={{ width: 300 }}
                  />
                )}
              />
            </FormControl>

            {/* Routing Selection */}
            <FormControl sx={{ minWidth: 300 }}>
              <Controller
                control={control}
                name={"webinarConfig.promotionRouting"}
                rules={{ required: "Please select a routing" }}
                render={({ field: routingField, fieldState: { error } }) => (
                  <FormControl sx={{ minWidth: 300 }} error={!!error}>
                    <InputLabel id="promotion-routing-label">
                      Select Routing
                    </InputLabel>
                    <Select
                      {...routingField}
                      labelId="promotion-routing-label"
                      id="promotion-routing"
                      label="Select Routing"
                      onChange={(e) => {
                        routingField.onChange(e.target.value);
                        if (e.target.value === "custom") {
                          // default field when switching to email based routing
                          setValue(
                            "webinarConfig.promotionRoutingField",
                            getValues("webinarConfig.promotionRoutingField") ??
                              "routing_field",
                          );
                        } else {
                          setValue("webinarConfig.promotionRoutingField", null);
                        }
                      }}
                      sx={{ width: 300 }}
                    >
                      <MenuItem value="sequential">Sequential</MenuItem>
                      <MenuItem value="custom">Email Based</MenuItem>
                    </Select>
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </FormControl>

            {/* Sender email field when email-based */}
            {watch("webinarConfig.promotionRouting" as any) === "custom" && (
              <FormControl sx={{ minWidth: 300 }}>
                <Controller
                  control={control}
                  name={"webinarConfig.promotionRoutingField" as any}
                  rules={{ required: "Please select a sender email field" }}
                  render={({ field: fieldSelect, fieldState: { error } }) => (
                    <FormControl sx={{ minWidth: 300 }} error={!!error}>
                      <InputLabel id="promotion-routing-field-label">
                        Send Email Field
                      </InputLabel>
                      <Select
                        {...fieldSelect}
                        labelId="promotion-routing-field-label"
                        id="promotion-routing-field"
                        label="Send Email Field"
                        sx={{ width: 300 }}
                      >
                        <MenuItem value="routing_field">Routing Field</MenuItem>
                        <MenuItem value="logic_field">Logic Field</MenuItem>
                      </Select>
                      {error && (
                        <FormHelperText>{error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </FormControl>
            )}
          </Stack>

          {/* Team Member List - Show when team is selected */}
          {selectedTeamId && (
            <Box sx={{ mt: 3 }}>
              <WebinarPromotionTeamList
                teamMembers={promotionTeamMembers || []}
                loading={loadingTeamMembers || updatingTeamMembers}
                templateId={templateId}
              />
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
